import React, { useEffect, useCallback } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { NoData } from '../RulesDetails/components/NoData';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { RuleStatusSwitch } from './RuleStatusSwitch';
import RulesUpdateModal from 'src/components/AddModals/RulesUpdateModal';
import { RuleSummaryItem } from './RuleSummaryItem';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { fetchScheduledActionGroups, fetchScheduledActionGroupsByEntity } from './utils';
import { IScheduledActionsGroup } from 'src/store/modules/adManager/adScheduledActionGroups/types';
import RuleStore from '../RulesDetails/stores';
import { useAppSelector } from 'src/utils/Hooks';
import SlMessagePlaceholder from 'src/components/BeaconV2Components/SlMessagePlaceholder/SlMessagePlaceholder';
import { useRuleData } from 'src/components/AddModals/RulesUpdateModal';

const ruleColumnDefs = [
  {
    headerName: 'Rule',
    valueFormatter: (_, data) => {
      return <RuleSummaryItem ruleName={data.shortDisplayName} ruleId={data.id} />;
    }
  },
  {
    headerName: 'Action',
    valueFormatter: (_, data) => {
      return data?.scheduleDetail?.action;
    }
  },
  {
    headerName: 'Time',
    valueFormatter: (_, data) => {
      return data?.scheduleDetail?.period;
    }
  },
  {
    headerName: 'Impact',
    valueFormatter: (_, data) => {
      return data?.scheduleDetail?.appliesTo;
    }
  },
  {
    headerName: 'Status',
    valueFormatter: (_, data) => RuleStatusSwitch({ data })
  }
];

const AdvertisingRules = () => {
  const { mainEntity } = useAppSelector((state) => state.entityService);
  const { rootData: ruleData } = useRuleData();

  const queryFn = useCallback(() => {
    if (mainEntity.type === 'adCampaign') {
      return fetchScheduledActionGroupsByEntity(mainEntity);
    } else {
      return fetchScheduledActionGroups(true);
    }
  }, [mainEntity]);

  const { data, isLoading } = useQuery({
    queryKey: ['scheduledActionGroups'],
    queryFn,
    refetchOnMount: true,
    staleTime: 0,
    refetchOnWindowFocus: true
  });

  const queryClient = useQueryClient();
  const { purgeRuleStore } = RuleStore((s) => ({
    purgeRuleStore: s.purgeRuleStore
  }));

  useEffect(() => {
    RulesUpdateModal.setRootData(data || []);
    purgeRuleStore();
  }, [data, purgeRuleStore]);

  useEffect(() => {
    RulesUpdateModal.setUpdateDataState((x: IScheduledActionsGroup[]) => {
      queryClient.setQueryData(['scheduledActionGroups'], x);
    });
  }, [queryClient]);

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <EntityTable
          columnDefs={ruleColumnDefs}
          rowData={
            ruleData?.sort((x, y) => {
              const cmp1 = new Date(x.scheduledActions[0].scheduledAction.createdTime);
              const cmp2 = new Date(y.scheduledActions[0].scheduledAction.createdTime);
              return cmp2.getTime() - cmp1.getTime();
            }) ?? []
          }
          isLoading={isLoading}
          shouldModifyColumnDefs={false}
          appTableProps={{
            noDataPlaceholder: (
              <div
                style={{
                  padding: '22.5px 8px'
                }}
              >
                <SlMessagePlaceholder
                  title="No rules yet?"
                  subtitle={<NoData />}
                  sx={{
                    '& .MuiTypography-subtitle1': {
                      fontSize: '16px'
                    },
                    '& .MuiTypography-body1': {
                      fontSize: '14px'
                    }
                  }}
                />
              </div>
            )
          }}
        />
      </Flex>
    </BasePageContainer>
  );
};

export default AdvertisingRules;
